/* @flow */
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import Gettext from "../common/Gettext";
import ClickOutside from "../common/ClickOutside";
import StockCategory from "./StockCategory";
import StocksTable from "./StocksTable";
import { InfoBlock } from "../Toolbar/ToolsContent";
import {
  ForexIcon,
  PopularStocksIcon,
  ForexOtcIcon,
  IndexesIcon,
  MetalsIcon,
  CommoditiesIcon,
  StocksIcon,
  CryptosIcon,
} from "./Icons";

import type { Stock } from "../../redux/CommonTypes";
import type { State as StockCategoriesType } from "../../redux/reducer/stockCategories";

const Table = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  padding-top: 17px;

  @media (max-width: 959px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: stretch;
    align-content: stretch;
    border-top: none;
    padding-top: 0;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  vertical-align: top;
  height: 100%;

  @media (max-width: 959px) {
    display: block;
  }
`;

const StockTitleList = styled(Column)`
  width: 164px;
  min-width: 164px;
  max-width: 164px;
  display: flex;
  justify-content: start;

  @media (max-width: 959px) {
    height: 100%;
    overflow: auto;
  }

  @media (max-width: 959px) and (min-width: 480px) {
    flex: 0 0 164px;
  }

  @media (max-width: 479px) {
    width: 138px;
    min-width: 138px;
    flex: 0 0 138px;
  }
`;

const StockList = styled.div`
  display: flex;
  justify-content: start;
  flex-grow: 0;
  flex-shrink: 0;
  width: 252px;
  min-width: 252px;
  border-left: 1px solid ${(props) => props.theme.mainDark};
  background-color: ${(props) => props.theme.popupDarkBackground};

  ${({ isTour }) =>
    isTour &&
    css`
      width: 100%;
    `}

  @media (max-width: 959px) {
    align-self: stretch;
    width: auto;
    min-width: auto;
    height: calc(100vh - 45px);
    border-left: none;
  }
`;

const Info = styled.div`
  @media (max-width: 959px) {
    padding-left: 10px;
    padding-right: 10px;
  }
`;

function filterObject(input, iteratee) {
  return Object.keys(input).reduce((output, key) => {
    const value = input[key];

    if (iteratee(value, key, input)) {
      return Object.assign(output, { [key]: value });
    }

    return output;
  }, {});
}

function filterPopular(input, profitFn) {
  return Object.keys(input)
    .filter((key) => input[key].enabled)
    .sort((a, b) => {
      // Sort by profit
      const profitA = profitFn(input[a]);
      const profitB = profitFn(input[b]);

      if (profitA === profitB) {
        // Sort alphabetically
        const titleA = input[a].title;
        const titleB = input[b].title;

        if (titleA > titleB) return 1;

        return -1;
      }

      if (profitA > profitB) return -1;

      return 1;
    })
    .slice(0, 10)
    .reduce((output, key) => {
      output[key] = input[key];

      return output;
    }, {});
}

function checkObjectField(assets, field) {
  for (let i in assets) {
    if (assets.hasOwnProperty(i)) {
      if (!assets[i][field]) {
        // TODO: what if assets[i][field] === false OR 0 OR ''? Then false anyway?
        return false;
      }
    }
  }
  return true;
}

const stockCategories = {
  popular: {
    title: <Gettext>Popular</Gettext>,
    Icon: <PopularStocksIcon />,
    info: (
      <Gettext>
        Here you can find quotations for the most popular assets on our
        platform.
      </Gettext>
    ),
  },
  forex: {
    title: <Gettext>Forex</Gettext>,
    Icon: <ForexIcon />,
    info: (
      <Gettext>
        Here you can find the currencies exchange rates from the Forex market.
        E.g., the rate of the euro to the US dollar.
      </Gettext>
    ),
  },
  cryptos: {
    title: <Gettext>cryptos</Gettext>,
    Icon: <CryptosIcon />,
    info: <Gettext>Here you can find cryptocurrencies rates.</Gettext>,
  },
  forex_otc: {
    title: <Gettext>Forex OTC</Gettext>,
    Icon: <ForexOtcIcon />,
    info: (
      <Gettext>
        Here you can find the currencies exchange rates from the Forex market.
        E.g., the rate of the euro to the US dollar.
      </Gettext>
    ),
  },
  indexes: {
    title: <Gettext>Indices</Gettext>,
    Icon: <IndexesIcon />,
    info: (
      <Gettext>
        Here you can find the Indices that are formed from multiple assets and
        show the status of some part of financial markets.
      </Gettext>
    ),
  },
  metals: {
    title: <Gettext>Metals</Gettext>,
    Icon: <MetalsIcon />,
    info: <Gettext>Here you can find rates for metals in USD.</Gettext>,
  },
  commodities: {
    title: <Gettext>Commodities</Gettext>,
    Icon: <CommoditiesIcon />,
    info: (
      <Gettext>
        Here you can find rates of commodities. E.g., WTI, which reflects
        futures prices for crude oil WTI (West Texas Intermediate).
      </Gettext>
    ),
  },
  us_stocks: {
    title: <Gettext>Stocks</Gettext>,
    Icon: <StocksIcon />,
    info: (
      <Gettext>
        Here you can find rates for stocks of such famous companies as Google,
        Amazon, Apple, etc.
      </Gettext>
    ),
  },
};

type Props = {
  stocks: {
    [key: string]: Stock,
  },
  tabs: Array<string>,
  categories: StockCategoriesType,
  activeStock: string,
  onStockSelect: (stock: string) => void,
  onClose: () => void,
  disabledOutside?: boolean,
  defaultCategory?: string | null,
};

function getDefaultCategory(assets, categories) {
  // Приоритет "forex" -> "forex_otc"
  let stocks = Object.keys(assets);

  let forexIsAvailable = stocks.some(
    (stock) => assets[stock].category === "forex" && assets[stock].enabled
  );

  if (forexIsAvailable) {
    return "forex";
  } else {
    return "forex_otc";
  }
}

function StockMenu(props: Props) {
  const [activeCategory, setActiveCategory] = useState(
    props.defaultCategory || getDefaultCategory(props.stocks, props.categories)
  );
  const [hoveredCategory, setHoveredCategory] = useState(null);

  const onStockCategoryClick = (category: string) => {
    setActiveCategory(category);
  };

  const showInfo = (category) => {
    setHoveredCategory(category);
  };

  const hideInfo = () => {
    setHoveredCategory(null);
  };

  useEffect(() => {
    props.dispatch({ type: "REQUEST_STOCK_ASSETS" });
  }, []);

  const activeAccount = props.accounts.find(
    (account) => account.id === props.profile.active_account_id
  );

  let assets;
  let getProfit;

  if (activeCategory === "popular") {
    if (props.tradeMode === "tournament") {
      getProfit = (item) => item.realProfit - item.profit_vip;
    } else {
      getProfit = (item) => item.realProfit;
    }

    assets = filterPopular(props.stocks, getProfit);
  } else {
    assets = filterObject(
      props.stocks,
      (stock) => stock.category === activeCategory
    );
  }

  const forexOtcIsFullyClosed = Object.keys(props.stocks)
    .map((key) => props.stocks[key])
    .filter((asset) => asset.category === "forex_otc")
    .every((asset) => !asset.enabled);

  return (
    <ClickOutside
      onOutsideClick={props.onClose}
      disabled={props.disabledOutside}
    >
      {({ innerRef }) => (
        <Table ref={innerRef}>
          <StockTitleList>
            <div>
              {props.categories
                .filter((category) =>
                  forexOtcIsFullyClosed ? category !== "forex_otc" : true
                )
                .map((category) => (
                  <StockCategory
                    key={category}
                    active={activeCategory === category}
                    category={category}
                    onClick={onStockCategoryClick}
                    showInfo={showInfo}
                    hideInfo={hideInfo}
                    {...stockCategories[category]}
                  />
                ))}
            </div>
            <Info>
              <InfoBlock>
                {hoveredCategory != null
                  ? stockCategories[hoveredCategory].info
                  : stockCategories[activeCategory].info}
              </InfoBlock>
            </Info>
          </StockTitleList>
          <StockList
            isTour={props.isTour}
            enabled={checkObjectField(assets, "enabled" && !props.isTour)}
            description={checkObjectField(
              assets,
              "description" && !props.isTour
            )}
          >
            <StocksTable
              assets={assets}
              tradeMode={props.tradeMode}
              tabs={props.tabs}
              hideName={activeCategory === "popular"}
              activeStock={props.activeStock}
              onStockClick={props.onStockSelect}
              activeAccount={activeAccount}
            />
          </StockList>
        </Table>
      )}
    </ClickOutside>
  );
}

function mapStateToProps(state) {
  return {
    tradeMode: state.settings.mode,
    stocks: state.stocks,
    tabs: state.tabs.tabs,
    categories: ["popular", ...state.stockCategories],
    activeStock: state.tabs.tabs[state.tabs.activeTab],
    profile: state.profile,
    accounts: state.accounts,
  };
}
export default connect(mapStateToProps)(StockMenu);



// WEBPACK FOOTER //
// ./src/components/StockMenu/StockMenu.js